import { useState } from 'react';
import { Save } from 'lucide-react';
import { Avatar } from '../components/common/Avatar';
import { useTheme } from '../hooks/useTheme';
import type { SandboxProfile } from '../types/sandbox';

interface SettingsPageProps {
  profile: SandboxProfile | null;
  onUpdate: (updates: Partial<SandboxProfile>) => Promise<void>;
}

export function SettingsPage({ profile, onUpdate }: SettingsPageProps) {
  const { theme, toggle } = useTheme();
  const [displayName, setDisplayName] = useState(profile?.display_name || '');
  const [bio, setBio] = useState(profile?.bio || '');
  const [saving, setSaving] = useState(false);

  if (!profile) {
    return (
      <div className="social-box rounded overflow-hidden">
        <div className="p-12 text-center">
          <p className="text-xs text-theme-muted">Sign in to access settings</p>
        </div>
      </div>
    );
  }

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate({
        display_name: displayName,
        bio,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-lg space-y-3 animate-fade-in">
      <div className="social-box rounded overflow-hidden">
        <div className="p-3 pb-0">
          <h2 className="text-lg font-bold mb-3" style={{ color: '#29487d' }}>Settings</h2>
        </div>
        <div className="p-4 space-y-4">
          {/* Avatar */}
          <div className="flex items-center gap-3">
            <Avatar name={displayName || profile.username} size="xl" />
            <div>
              <p className="text-sm font-bold text-theme-primary">@{profile.username}</p>
              <p className="text-2xs font-mono text-theme-muted">{profile.operator_did}</p>
            </div>
          </div>

          {/* Display Name */}
          <div>
            <label className="block text-xs font-bold text-theme-secondary mb-1">Display Name</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full h-8 px-2 rounded text-xs focus:outline-none"
              style={{
                background: 'var(--bg-input)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-input)',
              }}
            />
          </div>

          {/* Bio */}
          <div>
            <label className="block text-xs font-bold text-theme-secondary mb-1">Bio</label>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              rows={3}
              maxLength={280}
              className="w-full px-2 py-1.5 rounded text-xs focus:outline-none resize-none"
              style={{
                background: 'var(--bg-input)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-input)',
              }}
            />
            <p className="text-2xs text-theme-muted mt-0.5">{bio.length}/280</p>
          </div>
        </div>
      </div>

      {/* Theme */}
      <div className="social-box rounded overflow-hidden">
        <div className="box-header">Theme</div>
        <div className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-theme-primary">
                {theme === 'light' ? 'Light Mode (Retro)' : 'Dark Mode (BSL-4)'}
              </p>
              <p className="text-2xs text-theme-muted">
                {theme === 'light'
                  ? 'Classic Web 2.0 aesthetic'
                  : 'Cyberpunk containment aesthetic'
                }
              </p>
            </div>
            <button
              onClick={toggle}
              className="text-2xs font-bold px-3 py-1.5 rounded"
              style={{
                background: 'var(--accent)',
                color: 'var(--text-inverse)',
              }}
            >
              Switch to {theme === 'light' ? 'Dark' : 'Light'}
            </button>
          </div>
        </div>
      </div>

      {/* Theme Presets */}
      <div className="social-box rounded overflow-hidden">
        <div className="box-header">Theme Presets</div>
        <div className="p-3 grid grid-cols-3 gap-2">
          {[
            { name: 'Default', colors: ['#3b5998', '#ffffff'], free: true },
            { name: 'Cyberpunk', colors: ['#0a0e1a', '#8b5cf6'], free: false },
            { name: 'Matrix', colors: ['#001a00', '#00ff00'], free: false },
            { name: 'Retro 95', colors: ['#008080', '#c0c0c0'], free: false },
            { name: 'Neon', colors: ['#1a0033', '#ff00ff'], free: false },
            { name: 'Minimal', colors: ['#ffffff', '#000000'], free: true },
          ].map(t => (
            <div
              key={t.name}
              className="p-2 text-center cursor-pointer"
              style={{ border: '1px solid var(--border-separator)' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-separator)'; }}
            >
              <div className="flex gap-1 justify-center mb-1">
                <div className="w-4 h-4 rounded-sm" style={{ background: t.colors[0], border: '1px solid var(--border-card)' }} />
                <div className="w-4 h-4 rounded-sm" style={{ background: t.colors[1], border: '1px solid var(--border-card)' }} />
              </div>
              <div className="text-2xs font-bold" style={{ color: 'var(--text-primary)' }}>{t.name}</div>
              {!t.free && (
                <div className="text-2xs" style={{ color: '#ffd700' }}>$4.99</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Custom CSS (Premium) */}
      <div className="social-box rounded overflow-hidden">
        <div className="box-header">Custom CSS <span className="premium-badge">PRO</span></div>
        <div className="p-3">
          <textarea
            placeholder={"/* Write custom CSS here... */\n.social-box { border-radius: 8px; }"}
            className="w-full h-24 p-2 font-mono text-2xs focus:outline-none resize-none"
            style={{
              background: '#1e1e1e',
              color: '#00ff00',
              border: '1px solid var(--border-input)',
            }}
            disabled={!profile?.is_premium}
          />
          {!profile?.is_premium && (
            <p className="text-2xs mt-1" style={{ color: 'var(--text-muted)' }}>
              Upgrade to Premium to unlock custom CSS editing
            </p>
          )}
        </div>
      </div>

      {/* Save */}
      <button
        onClick={handleSave}
        disabled={saving}
        className="flex items-center gap-1.5 h-8 px-4 rounded text-xs font-bold transition-opacity disabled:opacity-40"
        style={{
          background: 'var(--accent)',
          color: 'var(--text-inverse)',
        }}
      >
        <Save className="w-3.5 h-3.5" />
        {saving ? 'Saving...' : 'Save Changes'}
      </button>
    </div>
  );
}
